import React, { useRef, useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import { capitalizeWords } from '../../../utils/formatters';
import { validateFileUpload, bankIsIndian } from './FormValidation';
import { StepHeader, StepNote } from './StepHeader';
import LockedField from './LockedField';
import BankVerifyPanel from './BankVerifyPanel';
import DocumentReadPanel from './DocumentReadPanel';
import FieldFlag from './FieldFlag';
import { useLocked } from '../hooks/useLocked';
import { fieldProps } from './missingFields';

const BankInformationStep = ({
  formData,
  updateFormData,
  errors,
  flaggedFields,
  onPreviewDocument,
  keepExistingDocuments,
  setKeepExistingDocuments,
  uploadedFiles,
  // Surepass bank verification (Indian vendors in the registration flow)
  kycActive = false,
  sessionId,
  identity,
  kyc,
  locked,
  lockSources,
  onKycResult,
}) => {
  const fileInputRef = useRef(null);

  // Indian bank account (IFSC) or not (SWIFT): the same test the validator uses to decide which code is required.
  const isIndian = bankIsIndian(formData);
  // The cancelled cheque is read here too, for the vendors the guided bank panel does not cover.
  const [proofFile, setProofFile] = useState(null);
  const canReadProof = isIndian && Boolean(sessionId) && kyc?.enabled !== false;
  const applyRead = (applied) => updateFormData(Object.fromEntries(applied.map((a) => [a.key, a.value])));
  const lock = useLocked(locked, lockSources, 3);
  const bankVerified = Boolean(identity?.bank?.verified_at);
  // The bank network could not help (down, timed out, or slow) and the vendor went on with the details
  // typed in — the server remembers it, so a refresh keeps the inputs open.
  const bankManual = !bankVerified && Boolean(identity?.bank_manual);
  // With verification active, the typed inputs only appear once the vendor has gone on by hand or the
  // feature is unavailable; otherwise the panel owns data entry.
  const featuresOff = kyc?.enabled === false || kyc?.features?.bank_pennyless === 'unavailable';
  const showManualInputs = !kycActive || bankVerified || bankManual || featuresOff;
  const lockedOrInput = (key, label, inputEl, extra = {}) =>
    lock.isLocked(key) ? <LockedField label={label} value={formData[key]} source={lock.sourceOf(key)} {...extra} /> : inputEl;

  // Updated account types - Cash Credit Account added for India only
  const accountTypes = [
    { value: 'savings', label: 'Savings Account' },
    { value: 'current', label: 'Current Account' },
    { value: 'od', label: 'Overdraft Account' },
    ...(isIndian ? [{ value: 'cash-credit', label: 'Cash Credit Account' }] : [])
  ];

  const handleInputChange = (field, value) => {
    // Auto-uppercase IFSC and Swift codes
    if (field === 'ifscCode' || field === 'swiftCode') {
      value = value.toUpperCase();
    }
    // Capitalize first letter of every word for specific fields
    const fieldsToCapitalize = ['bankName', 'branchName', 'bankAddress'];
    if (fieldsToCapitalize.includes(field)) {
      value = capitalizeWords(value);
    }
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  return (
    <div className="space-y-6">
      <StepHeader
        title="Bank Information"
        description={kycActive ? 'The account we pay into, confirmed by the bank itself — plus the cancelled cheque as proof.' : "The account we'll pay into, plus a bank proof document."}
      >
        {kycActive && bankVerified && (
          <StepNote tone="success" icon="ShieldCheck">
            Account details below were confirmed by your bank and cannot be edited. Use "Change account" on the card to start over.
          </StepNote>
        )}
        {kycActive && bankManual && (
          <StepNote tone="warning" icon="ShieldAlert">
            The bank could not be asked, so type your details exactly as they appear on your cancelled cheque — they are verified before approval.
          </StepNote>
        )}
      </StepHeader>

      {kycActive && (
        <div {...fieldProps('bankVerified')} tabIndex={-1} className="space-y-2 outline-none">
          <BankVerifyPanel
            sessionId={sessionId}
            identity={identity}
            kyc={kyc}
            formData={formData}
            onResult={onKycResult}
            onPreviewDocument={onPreviewDocument}
          />
          {!bankVerified && !showManualInputs && errors.bankVerified && (
            <p className="text-sm text-destructive">{errors.bankVerified}</p>
          )}
        </div>
      )}

      {showManualInputs && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {lockedOrInput('bankName', 'Bank Name', (
          <Input
            label="Bank Name"
            type="text"
            placeholder="Enter bank name"
            value={formData.bankName}
            {...fieldProps('bankName')}
            onChange={(e) => handleInputChange('bankName', e.target.value)}
            error={errors.bankName}
            required
          />
        ), { required: true })}

        {lockedOrInput('branchName', 'Branch Name', (
          <Input
            label="Branch Name"
            type="text"
            placeholder="Enter branch name"
            value={formData.branchName}
            {...fieldProps('branchName')}
            onChange={(e) => handleInputChange('branchName', e.target.value)}
            error={errors.branchName}
            required={isIndian}
          />
        ))}

        {lockedOrInput('accountNumber', 'Account Number', (
          <Input
            label="Account Number"
            type="text"
            placeholder="Enter account number"
            value={formData.accountNumber}
            {...fieldProps('accountNumber')}
            onChange={(e) => handleInputChange('accountNumber', e.target.value)}
            error={errors.accountNumber}
            required
          />
        ), { required: true, displayValue: formData.accountNumber ? `••••${String(formData.accountNumber).slice(-4)}` : '' })}

        {!bankVerified && (
          <Input
            label="Confirm Account Number"
            type="text"
            placeholder="Re-enter account number"
            value={formData.confirmAccountNumber}
            {...fieldProps('confirmAccountNumber')}
            onChange={(e) => handleInputChange('confirmAccountNumber', e.target.value)}
            error={errors.confirmAccountNumber}
            required
          />
        )}
        {bankVerified && lock.isLocked('accountHolderName') && (
          <LockedField label="Account Holder" value={formData.accountHolderName} source="bank" />
        )}

        {isIndian ? (
          <>
            {lockedOrInput('ifscCode', 'IFSC Code', (
              <div>
                <Input
                  label="IFSC Code"
                  type="text"
                  placeholder="Enter IFSC code"
                  value={formData.ifscCode}
                  {...fieldProps('ifscCode')}
                  onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                  error={errors.ifscCode}
                  required
                />
                <FieldFlag flaggedFields={flaggedFields} name="ifscCode" />
              </div>
            ), { required: true })}

            {lock.isLocked('accountType') ? (
              <LockedField label="Account Type" displayValue={(accountTypes.find((o) => o.value === formData.accountType) || {}).label || formData.accountType} source="bank" required />
            ) : (
              <Select
                label="Account Type"
                options={accountTypes}
                value={formData.accountType}
                {...fieldProps('accountType')}
                onChange={(value) => handleInputChange('accountType', value)}
                error={errors.accountType}
                required
              />
            )}
          </>
        ) : (
          <>
            <Input
              label="Swift Code"
              type="text"
              placeholder="Enter Swift code (8-11 characters)"
              value={formData.swiftCode}
              {...fieldProps('swiftCode')}
              onChange={(e) => handleInputChange('swiftCode', e.target.value)}
              error={errors.swiftCode}
              required
              description="Swift code is required for foreign vendors (8-11 characters, letters and numbers only)"
            />

            <Select
              label="Account Type"
              options={accountTypes}
              value={formData.accountType}
              {...fieldProps('accountType')}
              onChange={(value) => handleInputChange('accountType', value)}
              error={errors.accountType}
              required
            />
          </>
        )}

        <div className="md:col-span-2">
          {lockedOrInput('bankAddress', 'Bank Address', (
            <Input
              label="Bank Address"
              type="text"
              placeholder="Enter complete bank address"
              value={formData.bankAddress}
              {...fieldProps('bankAddress')}
              onChange={(e) => handleInputChange('bankAddress', e.target.value)}
              error={errors.bankAddress}
              required
            />
          ), { required: true })}
        </div>

        {/* Bank Proof Upload */}
        <div className="md:col-span-2 bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">Bank Proof Document</h3>
          <p className="text-sm text-text-secondary mb-4">
            {isIndian 
              ? "Upload a cancelled cheque or bank statement (PDF, JPG, PNG - Max 4MB)"
              : "Upload signed and stamped letterhead or official bank document (PDF, JPG, PNG - Max 4MB)"
            }
          </p>

          {/* Show checkbox if document is pre-filled */}
          {uploadedFiles?.BANK_PROOF && !formData.bankProof?.name && (
            <div className="mb-4 p-3 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="keepBankProof"
                    checked={keepExistingDocuments.bankProof || false}
                    onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, bankProof: e.target.checked }))}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                  <label htmlFor="keepBankProof" className="text-sm text-foreground cursor-pointer">
                    Keep existing: <span className="font-medium">{uploadedFiles.BANK_PROOF.file_name}</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.BANK_PROOF, 'BANK_PROOF')}
                  className="px-3 py-1 text-sm text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors flex items-center space-x-1"
                >
                  <Icon name="eye" className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>
          )}

          {(!keepExistingDocuments.bankProof || !uploadedFiles?.BANK_PROOF) && (
            <FileUpload
            label={
              isIndian
                ? "Cancelled Cheque"
                : "Signed and Stamped letter head of the BANK"
            }
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={4 * 1024 * 1024}
            {...fieldProps('bankProof')}
            value={formData.bankProof}
            onChange={(file) => { handleFileUpload('bankProof', file); setProofFile(file || null); }}
            onRemove={() => { handleRemoveFile('bankProof'); setProofFile(null); }}
            onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'BANK_PROOF')}
            error={errors.bankProof}
            required
            description="Max 4MB - PDF, JPG, or PNG format"
          />
          )}
          {canReadProof && proofFile && (
            <DocumentReadPanel
              sessionId={sessionId}
              kind="BANK_PROOF"
              file={proofFile}
              onApplied={applyRead}
              confirmedBy={formData.email || formData.directorEmail || ''}
              onCleared={() => setProofFile(null)}
              className="mt-3"
            />
          )}
        </div>
      </div>
      )}

    </div>
  );
};

export default BankInformationStep;